import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Check, Radio } from 'lucide-react';
import {
  LiveState, LiveQuestion, getDeviceToken, fetchLiveState, castLiveVote,
  subscribeLiveState, unsubscribe,
} from '../services/liveService';

const getText = (t: any): string => (t && (t.en || Object.values(t)[0])) || '';

const LiveAudienceView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<LiveState>({ phase: 'LOBBY', question: null });
  const [loading, setLoading] = useState(true);
  const [voted, setVoted] = useState<string | null>(null); // value voted for the current question
  const [busy, setBusy] = useState<string | null>(null);
  const token = useRef(getDeviceToken());
  const qIdRef = useRef<string | null>(null);

  // Apply a new state; reset the local vote when the active question changes.
  const apply = (s: LiveState) => {
    const qid = s.question?.id ?? null;
    if (qid !== qIdRef.current) { qIdRef.current = qid; setVoted(null); }
    setState(s);
  };

  useEffect(() => {
    let alive = true;
    (async () => { try { apply(await fetchLiveState(id!)); } finally { if (alive) setLoading(false); } })();

    // Instant updates via broadcast; slow poll as a fallback for missed events / late joiners.
    const channel = subscribeLiveState(id!, (s) => apply(s));
    const poll = setInterval(async () => { try { apply(await fetchLiveState(id!)); } catch { /* ignore */ } }, 6000);

    return () => { alive = false; clearInterval(poll); unsubscribe(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const vote = async (q: LiveQuestion, value: any) => {
    setBusy(String(value));
    try {
      const res = await castLiveVote(id!, q.id, value, token.current);
      if (res.ok) setVoted(String(value));
    } catch (e) { console.error(e); }
    finally { setBusy(null); }
  };

  if (loading) return <Center><Loader2 className="animate-spin text-indigo-400" size={36} /></Center>;

  const q = state.question;

  if (state.phase === 'LOBBY' || !q) {
    return <Center>
      <Radio className="text-indigo-400 animate-pulse mb-4" size={48} />
      <h1 className="text-2xl font-bold text-white">You're in!</h1>
      <p className="text-slate-400 mt-1">Waiting for the host to start…</p>
    </Center>;
  }

  if (state.phase === 'ENDED') {
    return <Center>
      <Check className="text-emerald-400 mb-4" size={48} />
      <h1 className="text-2xl font-bold text-white">Thanks for taking part!</h1>
      <p className="text-slate-400 mt-1">The live session has ended.</p>
    </Center>;
  }

  // QUESTION phase
  const isRating = q.question_type === 'RATING';
  const options: { value: any; label: string }[] = isRating
    ? ratingValues(q).map((v) => ({ value: v, label: String(v) }))
    : (q.config?.options || []).map((o: any) => ({ value: o.id, label: getText(o.label) }));

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col p-5">
      <div className="text-center py-4">
        <span className="text-indigo-300 text-xs font-bold flex items-center justify-center gap-1.5"><Radio size={12} className="animate-pulse" /> LIVE</span>
        <h1 className="text-2xl font-black text-white mt-2 leading-tight">{getText(q.question_text)}</h1>
        {voted && <p className="text-emerald-400 text-sm mt-2 flex items-center justify-center gap-1"><Check size={14} /> Vote counted — tap another to change</p>}
      </div>

      <div className={`flex-1 grid gap-3 ${isRating ? 'grid-cols-3 content-center' : 'grid-cols-1 content-start'}`}>
        {options.map((o, i) => {
          const sel = voted === String(o.value);
          return (
            <button key={i} onClick={() => vote(q, o.value)} disabled={busy !== null}
              className={`rounded-2xl font-bold transition-all active:scale-95 flex items-center justify-center gap-2 ${isRating ? 'aspect-square text-3xl' : 'p-5 text-xl text-left justify-start'} ${
                sel ? 'bg-indigo-600 text-white ring-4 ring-indigo-400/50' : 'bg-slate-800 text-slate-100 hover:bg-slate-700'
              }`}>
              {busy === String(o.value) ? <Loader2 className="animate-spin" size={20} /> : <>
                {sel && <Check size={18} />} {o.label}
              </>}
            </button>
          );
        })}
      </div>
      <p className="text-center text-slate-600 text-xs py-3">Powered by Tinaney</p>
    </div>
  );
};

const Center: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-center p-6">{children}</div>
);

function ratingValues(q: LiveQuestion): number[] {
  const min = q.config?.min ?? 1, max = q.config?.max ?? 5;
  const r: number[] = [];
  for (let v = min; v <= max; v++) r.push(v);
  return r;
}

export default LiveAudienceView;
